import { useCallback, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { debounce } from "@/utils/debounce";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogOverlay, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Search, Coins } from "lucide-react";
import { toast } from "react-hot-toast";
import InfiniteScroll from "react-infinite-scroll-component";
import AddSongDialogSkeleton from "../playlist/AddSongDialogSkeleton";
import imgGreenBird from '../../assets/greenbird.png';
import { Music4 } from "lucide-react";
import { SET_LAUNCH_DIALOG_OPEN } from "@/redux/features/music/leaderboardSlice";
import { SET_SHOW_LOGIN } from "@/redux/features/auth/authSlice";
import { SET_LAUNCHTOKENPLAYLIST, SET_LAUNCHTOKENSONG, SET_WALLETBTN } from "@/redux/features/music/musicSlice";
import PUMPFUN from "../../assets/pumpfun.webp"
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

const LaunchSongDialog = () => {
  const dispatch = useDispatch();
  const { playling } = useSelector((state) => state.music);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [noResultMsg, setNoResultMsg] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [hasMoreSearch, setHasMoreSearch] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const [dialogSongs, setDialogSongs] = useState([]);
  const [dialogPage, setDialogPage] = useState(1);
  const [isDialogLoading, setIsDialogLoading] = useState(false);
  const { launchDialogOpen } = useSelector((state) => state.leaderboard);
  const { user } = useSelector((state) => state.auth);

  useEffect(() => {
    if (launchDialogOpen) {
      setDialogSongs([]);
      setHasMore(true);
      setDialogPage(1);
      setSearchTerm("");
      setSearchResults([]);
      setSearchPage(1);
      setHasMoreSearch(true);
      setNoResultMsg(false);
      setIsDialogLoading(false);
      setIsSearching(false);
    }
  }, [launchDialogOpen]);

  useEffect(() => {
    if (launchDialogOpen && dialogSongs.length === 0 && hasMore && !isDialogLoading) {
      fetchMoreSongs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [launchDialogOpen, dialogSongs.length, hasMore, isDialogLoading]);

  useEffect(() => {
    const token = window.localStorage.getItem("token");
    const debouncedSearch = debounce(async () => {
      if (!searchTerm) {
        setSearchResults([]);
        setNoResultMsg(false);
        setHasMoreSearch(true);
        setSearchPage(1);
        return;
      }

      if (searchPage === 1) {
        setSearchResults([]);
      }
      setIsSearching(true);
      try {
        const response = await axios.get(
          `/songsmenus?search=${searchTerm}&page=${searchPage}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const newSongs = response.data.songs || [];

        setSearchResults(prev =>
          searchPage === 1 ? newSongs : [...prev, ...newSongs]
        );
        setHasMoreSearch(newSongs.length === 20);
        setNoResultMsg(searchPage === 1 && newSongs.length === 0);
      } catch (error) {
        console.error("Search failed:", error);
        toast.error("Failed to search songs");
        setHasMoreSearch(false);
      } finally {
        setIsSearching(false);
      }
    }, 500);

    debouncedSearch();
    return () => debouncedSearch.cancel();
  }, [searchTerm, searchPage]);

  const fetchMoreSongs = useCallback(async () => {
    if (isDialogLoading || !hasMore) return;
    setIsDialogLoading(true);
    const currentPageToFetch = dialogPage;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`/allsongs?page=${currentPageToFetch}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newSongs = response.data.songs || [];

      if (newSongs.length === 0 || newSongs.length < 20) {
        setHasMore(false);
      } else {
        setDialogPage(currentPageToFetch + 1);
      }

      setDialogSongs(prevSongs => {
        const existingSongIds = new Set(prevSongs.map(song => song.song_id));
        const uniqueNewSongs = newSongs.filter(song => !existingSongIds.has(song.song_id));
        return [...prevSongs, ...uniqueNewSongs];
      });
    } catch (error) {
      console.error('Error fetching more songs:', error);
      toast.error("Failed to load more songs");
      setHasMore(false);
    } finally {
      setIsDialogLoading(false);
    }
  }, [dialogPage, hasMore, isDialogLoading]);

  const loadMoreDialogSongs = useCallback(() => {
    fetchMoreSongs();
  }, [fetchMoreSongs]);

  const loadMoreSearchResults = useCallback(() => {
    if (!isSearching && hasMoreSearch) {
      setSearchPage(prev => prev + 1);
    }
  }, [isSearching, hasMoreSearch]);

  const handleWalletConnect = (song) => {
    if (!user || user?.is_guest) {
      dispatch(SET_SHOW_LOGIN(true));
      dispatch(SET_LAUNCH_DIALOG_OPEN(false));
      return toast.error("Login to launch token", { id: "walletConnect" });
    }
    dispatch(SET_LAUNCHTOKENSONG(song));
    dispatch(SET_LAUNCHTOKENPLAYLIST(null));
    dispatch(SET_WALLETBTN(true));
    dispatch(SET_LAUNCH_DIALOG_OPEN(false));
    setDialogSongs([]);
    setSearchResults([]);
    setSearchPage(1);
  }

  return (
    <Dialog open={launchDialogOpen} onOpenChange={() => dispatch(SET_LAUNCH_DIALOG_OPEN(false))}>
      <DialogOverlay className="z-[999]">
        <DialogContent className={`z-[9999] w-[95%] p-3 sm:p-8 max-w-3xl max-h-[80vh] sm:max-h-[80vh] overflow-hidden bg-gradient-to-br from-slate-950 via-slate-800 to-slate-950 dark:bg-gradient-to-br dark:from-pink-100  dark:to-pink-300 border-1 border border-green-400/30 dark:border-pink-800 rounded-sm shadow-2xl shadow-green-900/20 text-slate-300 ${playling ? 'mb-44' : ''}`}>
          <DialogHeader className="space-y-2">
            <div className="flex items-center space-x-3">
              <Music4 className="text-green-400 dark:text-pink-900" size={28} />
              <DialogTitle className="text-2xl text-start font-bold text-green-400 dark:text-pink-900">
                Launch Song Token
              </DialogTitle>
            </div>
            <DialogDescription className="text-start text-slate-400 dark:text-pink-800">
              Select a song to launch its token
            </DialogDescription>
          </DialogHeader>

          <div className="relative mb-4">
            <Input type="text" placeholder="Search songs..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-12 pr-4 py-3 bg-slate-800/60 dark:bg-pink-100 backdrop-blur-md border-green-400/30 dark:border-pink-800 rounded-sm focus:ring-2 dark:focus-ring-1 focus:ring-green-400/50 dark:focus:ring-pink-400 text-slate-200 dark:text-slate-900 dark:placeholder:text-slate-900" />
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400 dark:text-pink-800" size={24} />
          </div>

          <div id="scrollableDiv" className="overflow-y-auto max-h-[60vh] pb-12 z-[999] custom-scrollbar scrollbar-hide">
            {(isDialogLoading && searchTerm === "" && dialogSongs.length === 0) || (isSearching && searchTerm !== "" && searchResults.length === 0) ? (
              <AddSongDialogSkeleton />
            ) : searchTerm === "" ? (
              <InfiniteScroll
                dataLength={dialogSongs.length}
                next={loadMoreDialogSongs}
                hasMore={hasMore}
                loader={isDialogLoading ? <AddSongDialogSkeleton /> : null}
                scrollableTarget="scrollableDiv"
                style={{ overflow: 'hidden' }}
              >
                {dialogSongs.map((song, index) => (
                  <SongItem key={`${song.song_id}-dialog-${index}`} song={song} onLaunchClick={handleWalletConnect} user={user} />
                ))}
                {isDialogLoading && dialogSongs.length > 0 && <AddSongDialogSkeleton count={1} />}
              </InfiniteScroll>
            ) : (
              <>
                <InfiniteScroll
                  dataLength={searchResults.length}
                  next={loadMoreSearchResults}
                  hasMore={hasMoreSearch}
                  loader={isSearching ? <AddSongDialogSkeleton /> : null}
                  scrollableTarget="scrollableDiv"
                  style={{ overflow: 'hidden' }}
                >
                  {searchResults.map((song, index) => (
                    <SongItem key={`${song.song_id}-search-${index}`} song={song} onLaunchClick={handleWalletConnect} user={user} />
                  ))}
                  {isSearching && searchResults.length > 0 && <AddSongDialogSkeleton count={1} />}
                </InfiniteScroll>

                {noResultMsg && searchResults.length === 0 && !isSearching && (
                  <div className="flex flex-col items-center justify-center p-8 space-y-4">
                    <Music4 className="text-green-400 opacity-50" size={48} />
                    <p className="text-slate-400 text-center">
                      No songs found matching your search.
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </DialogContent>
      </DialogOverlay>
    </Dialog>
  );
};

const SongItem = ({ song, onLaunchClick, user }) => {
  // const [showDisabledTooltip, setShowDisabledTooltip] = useState(false);
  const isCreator = user?.username && song.username && user.username === song.username;

  // check if the song is older than 24 hours, bypass if user is creator
  const calculateTimeDisabled = (isCreatorFlag) => {
    if (isCreatorFlag) {
      // Creator can launch immediately
      return { isDisabled: false, diffMs: 0 };
    }
    if (!song?.date_time) {
      // If no date_time, assume not disabled (or handle as error case if preferred)
      return { diffHours: 0, isDisabled: false };
    }
    const backendTime = new Date(song.date_time + (song.date_time.endsWith('Z') ? '' : 'Z')); // Force UTC
    const currentUTCTime = new Date();
    const diffMs = currentUTCTime - backendTime;
    // Disable only if NOT creator AND time difference is less than 24 hours
    const isDisabled = diffMs < 24 * 60 * 60 * 1000;
    return { isDisabled, diffMs };
  };

  // Pass isCreator to the calculation function
  // eslint-disable-next-line no-unused-vars
  const { isDisabled, diffMs } = calculateTimeDisabled(isCreator);

  return (
    <div className="flex items-center justify-between py-1 px-2 bg-slate-900/50 dark:bg-gradient-to-br dark:from-pink-200 dark:via-pink-300 dark:to-pink-400 hover:bg-slate-900 dark:hover:from-pink-100 dark:hover:via-pink-200 dark:hover:to-pink-300 backdrop-blur-sm rounded-lg mb-2">
      <div className="flex items-center space-x-2 sm:space-x-4">
        <LazyLoadImage
          effect="blur"
          src={song.image_url || imgGreenBird}
          alt={song.song_name}
          className="sm:w-12 sm:h-12 w-10 h-10 rounded object-cover"
          placeholderSrc={imgGreenBird}
        />
        <div className="flex flex-col gap-1">
          <h4 className="truncate max-w-[120px] sm:max-w-full font-sm text-xs sm:text-md sm:font-medium text-white dark:text-slate-900">
            {song.song_name}
          </h4>
          <p className="truncate max-w-[120px] text-xs sm:text-sm text-slate-400 dark:text-pink-800">
            {song.username || "Unknown Artist"}
          </p>
        </div>
      </div>
      {song.token ? (
        <a
          href={song.token}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-1 bg-blue-500 hover:bg-blue-400 w-24 sm:min-w-[110px] dark:bg-indigo-600 dark:hover:bg-indigo-600/90 text-white rounded-md text-sm font-medium px-4 sm:py-2 py-1.5 transition-colors duration-150"
        >
          <LazyLoadImage effect="blur" src={PUMPFUN} alt="pumpfun" className="w-5 h-5" /> Buy
        </a>
      ) : (
        <div className="group relative" style={{ position: 'relative' }}>
          <button
            onClick={() =>  onLaunchClick(song)}
            // disabled={isDisabled}
              // onMouseOver={() => setShowDisabledTooltip(true)}
              // onMouseOut={() => setShowDisabledTooltip(false)}
            className={`inline-flex items-center justify-center gap-1.5 px-4 sm:py-2 py-1.5 w-24 sm:min-w-[110px] text-sm font-medium rounded-sm transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-900 bg-green-500 hover:bg-green-400 dark:bg-pink-600 dark:hover:bg-pink-500 text-black dark:text-white focus:ring-green-400 dark:focus:ring-pink-500`}
          >
            <Coins className={`w-5 h-5`} />
            Launch
          </button>
          
          {/* {isDisabled && showDisabledTooltip && (
            <div className="absolute bottom-4 sm:left-4 -left-2 -translate-x-1/2 mb-2 px-3 py-2 bg-black text-white text-xs rounded whitespace-nowrap z-[9999]">
              Check back in {Math.ceil(diffMs / (1000 * 60 * 60))} hours to launch token
            </div>
          )} */}
        </div>
      )}
    </div>
  );
};

export default LaunchSongDialog;